<template>
  <v-card flat class="mx-1 my-2 px-3 transparent">
    <v-layout mt-2>
      <v-flex xs1 v-if="!funderView">
        <v-layout column fill-height justify-center>
          <v-img :src="logo" height="25px" contain></v-img>
        </v-layout>
      </v-flex>
      <v-flex xs8>
        <v-card-title class="pb-4">
          <v-layout column fill-height mx-2>
            <div
              v-if="funderView"
              class="subtitle-1"
              style="font-weight: 600;text-transform: uppercase;letter-spacing:0.03em"
            >
              {{ enquiry.customer.name }}
            </div>
            <div v-else class="subtitle-1" style="font-weight: 600;text-transform: uppercase;letter-spacing:0.03em">
              {{ enquiry.product.name }}
            </div>
            <v-layout column>
              <v-flex class="py-0">
                <div class="subtitle-2 grey--text text--darken-1">
                  <span style="font-weight: 600">Requested:</span> {{ enquiry.funding_requested | currency }}
                </div>
              </v-flex>
              <v-flex class="py-0">
                <div class="subtitle-2 grey--text text--darken-1" v-if="fundingOffered">
                  <span style="font-weight: 600">Offered:</span> {{ enquiry.funding_offered | currency }}
                </div>
              </v-flex>
              <v-flex xs4 class="py-0">
                <div class="subtitle-2 grey--text text--darken-1" v-if="fundingObtained">
                  <span style="font-weight: 600">Obtained:</span> {{ enquiry.funding_obtained | currency }}
                </div>
              </v-flex>
              <v-flex class="py-0" v-if="!!enquiry.monthly_interest_rate">
                <div class="subtitle-2 grey--text text--darken-1">
                  <span style="font-weight: 600">Monthly Interest Rate:</span> {{ enquiry.monthly_interest_rate }}
                </div>
              </v-flex>
              <v-flex class="py-0" v-if="enquiry.provider_reference">
                <div class="subtitle-2 grey--text text--darken-1">
                  <span style="font-weight: 600">Funder Reference:</span> {{ enquiry.provider_reference }}
                </div>
              </v-flex>
            </v-layout>
          </v-layout>
        </v-card-title>
      </v-flex>
      <v-flex xs3>
        <v-layout column align-end>
          <v-flex class="text-right">
            <div class="caption text--secondary">Created: {{ enquiry.created_at | moment('DD/MM/YYYY') }}</div>
            <v-menu offset-y>
              <template v-slot:activator="{ on }">
                <v-btn :color="colour" depressed class="px-3 mx-0 white--text" v-on="on" small rounded>
                  {{ enquiry.status.name }}
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item v-for="(status, index) in visibleStatuses" :key="index" @click="updateStatus(status.id)">
                  <v-list-item-title>{{ status.name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-tooltip v-if="enquiry.status.name === 'FUNDING RELEASED'" bottom>
              <template v-slot:activator="{ on }">
                <v-btn fab class="options-btn" depressed :color="colour" @click="editEnquiry" v-on="on">
                  <v-icon size="12" color="white">fa-edit</v-icon>
                </v-btn>
              </template>
              <span>Edit Enquiry</span>
            </v-tooltip>
            <v-tooltip v-if="enquiry.status.name === 'FUNDING RELEASED'" bottom>
              <template v-slot:activator="{ on }">
                <v-btn fab class="options-btn" depressed :color="colour" @click="duplicate" v-on="on">
                  <v-icon size="12" color="white">fa-copy</v-icon>
                </v-btn>
              </template>
              <span>Add Subsequent Enquiry</span>
            </v-tooltip>
          </v-flex>
          <v-flex class="text-right pt-1">
            <v-icon v-if="enquiry.revolving" title="Revolving deal">sync</v-icon>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
    <v-layout column v-if="enquiry.feedback">
      <v-flex class="px-5" xs12 v-for="error in enquiry.feedback.errors" :key="error.id">
        <v-alert type="error" dense prominent border="top" colored-border elevation="3">
          <p>{{ error.detail }}</p>

          <div  v-if="error.requestParams">
            <p>The following parameters were sent in the request:</p>
            <v-simple-table dense>
              <template v-slot:default>
                <tbody>
                <tr
                  v-for="(value, key) in error.requestParams"
                  :key="key"
                >
                  <td class="pl-0">{{ key }}</td>
                  <td class="px-0">{{ value }}</td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>

        </v-alert>
      </v-flex>
      <v-flex class="px-5" xs12 v-if="enquiry.feedback.info">
        <v-alert
          :type="infoAlertType"
          dense
          prominent
          border="top"
          elevation="3"
          colored-border
          :color="infoAlertColour"
        >
          <v-simple-table>
            <tbody>
              <tr v-for="item in enquiry.feedback.info" :key="item.key">
                <td>{{ item.key }}</td>
                <td>{{ item.value }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-alert>
      </v-flex>
      <enquiry-quote
        v-if="enquiry.feedback.quote"
        :productType="enquiry.product.type"
        :quote="enquiry.feedback.quote"
      ></enquiry-quote>
    </v-layout>
    <v-divider v-if="!last"></v-divider>
    <enquiry-funding-offered-dialog
      :show.sync="showFundingOffered"
      :enquiry="enquiry"
      :status_id="status_id"
    ></enquiry-funding-offered-dialog>
    <enquiry-funding-released-dialog
      :show.sync="showFundingReleased"
      :enquiry="enquiry"
      :status_id="status_id"
      :duplicateEnquiry="duplicateEnquiry"
    />
  </v-card>
</template>

<script>
import _ from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import EnquiryFundingOfferedDialog from '../EnquiryActions/EnquiryFundingOfferedDialog';
import EnquiryFundingReleasedDialog from '../EnquiryActions/EnquiryFundingReleasedDialog';
import EnquiryQuote from './EnquiryQuote';

export default {
  components: {
    EnquiryFundingOfferedDialog,
    EnquiryFundingReleasedDialog,
    EnquiryQuote
  },
  name: 'enquiry-list-tile',
  props: ['enquiry', 'funderView'],
  data: () => ({
    last: false,
    showFundingReleased: false,
    showFundingOffered: false,
    duplicateEnquiry: false,
    status_id: '',
    brokerExcludedStatuses: [{ name: 'EXPIRED' }, { name: 'ARCHIVED' }, { name: 'AUTO CLOSED' }]
  }),
  computed: {
    ...mapGetters(['EnquiryStatuses', 'StatusColours', 'UserHasRole']),
    fundingOffered() {
      return parseFloat(this.enquiry.funding_offered) > 0;
    },
    fundingObtained() {
      return parseFloat(this.enquiry.funding_obtained) > 0;
    },
    fundingReleased() {
      return this.enquiry.status.name === 'FUNDING RELEASED';
    },
    status() {
      return this.enquiry.status.id;
    },
    logo() {
      return this.enquiry.product.logo === null
        ? 'https://s3-eu-west-1.amazonaws.com/media.alternativebusinessfunding.co.uk/public/missing.png'
        : this.enquiry.product.logo;
    },
    colour() {
      let status = _.find(this.StatusColours, ['status', this.enquiry.status.name]);
      if (_.isUndefined(status)) {
        status = { colour: 'light-grey' };
      }
      return status.colour;
    },
    visibleStatuses() {
      let statuses = this.EnquiryStatuses;
      if (this.UserHasRole('Property Broker')) {
        statuses = _.filter(statuses, status => !_.some(this.brokerExcludedStatuses, { name: status.name }));
      }
      return statuses;
    },
    infoAlertType() {
      if (this.enquiry.state === 'Active') {
        return 'success';
      }
      return 'warning';
    },
    infoAlertColour() {
      if (this.enquiry.state === 'Active') {
        return 'success';
      }
      return '#FB8C00';
    }
  },
  methods: {
    ...mapActions(['storeCustomerEnquiry', 'getEnquiryCalculatedFee']),
    updateStatus(status) {
      switch (status) {
        case '02a8a6f9-054c-43ea-88b9-371862280814': // FUNDING RELEASED
          this.showFundingReleased = true;
          this.duplicateEnquiry = false;
          this.status_id = status;
          break;
        case 'bb9b3c01-0961-4481-a7e5-5280ef88b74b': // FUNDER OFFER MADE
          this.showFundingOffered = true;
          this.status_id = status;
          break;
        default:
          this.storeCustomerEnquiry({
            id: this.enquiry.id,
            updates: {
              status_id: status
            }
          });
      }
    },
    editEnquiry() {
      this.duplicateEnquiry = false;
      this.showFundingReleased = true;
    },
    duplicate() {
      this.duplicateEnquiry = true;
      this.showFundingReleased = true;
    }
  },
  filters: {
    currency: function(value) {
      const intValue = parseFloat(value);

      return (
        '£' +
        intValue.toLocaleString({
          style: 'currency',
          currency: 'GBP',
          currencyDisplay: 'symbol',
          minimumSignificantDigits: 0,
          maximumSignificantDigits: 0
        })
      );
    }
  },
  mounted() {
    this.last = this.$parent.$children[this.$parent.$children.length - 1] === this;
  }
};
</script>

<style lang="scss" scoped>
.options-btn {
  height: 30px;
  width: 30px;
  margin: 6px 0 6px 5px;
}
.v-avatar {
  img {
    width: 100%;
    height: auto;
    margin-top: auto;
    margin-bottom: auto;
  }
}
</style>
